import { supabase } from "./supabase";
import { plainError, unreachableText } from "./plainError";
const BASE = (import.meta.env.VITE_API_URL as string) || "";

/** An API failure that still knows its HTTP status.
 *
 *  Without it, every failure reads the same — which is how a 500 on `/portal/whoami` came to be
 *  rendered as "your account doesn't have platform access". A staff member locked out during an
 *  outage, told it was a permissions problem. `status === 0` means the request never reached the
 *  server. Mirrors dashboard/src/lib/api.ts. */
export class ApiError extends Error {
  status: number;
  /** What actually came back, for logs -- `message` is the sentence for a person (lib/plainError.ts). */
  raw: string;
  constructor(message: string, status: number, raw = message) { super(message); this.name = "ApiError"; this.status = status; this.raw = raw; }
}
async function failed(res: Response): Promise<ApiError> {
  const body = await res.text().catch(() => "");
  return new ApiError(plainError(res.status, body), res.status, `API ${res.status}: ${body || res.statusText}`);
}
function unreachable(e: any): ApiError {
  return new ApiError(unreachableText(false), 0, e?.message || "network error");
}
export async function api<T = any>(path: string, opts: RequestInit = {}): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...opts,
      headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) },
    });
  } catch (e: any) { throw unreachable(e); }
  if (!res.ok) throw await failed(res);
  return res.json() as Promise<T>;
}

export async function apiForm<T = any>(path: string, form: FormData): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      method: "POST", body: form,
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) }, // no Content-Type: browser sets the multipart boundary
    });
  } catch (e: any) { throw unreachable(e); }
  if (!res.ok) throw await failed(res);
  return res.json() as Promise<T>;
}
